import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signIn, signInWithRedirect } from "../amplify";
import PublicShell from "../layout/PublicShell";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui";
import { motion } from "framer-motion";

export default function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await signIn({ username: email, password });
      if (res.isSignedIn) nav("/app/today", { replace: true });
      else if (res.nextStep?.signInStep === "CONFIRM_SIGN_UP")
        nav(`/verify?email=${encodeURIComponent(email)}`, { replace: true });
      else
        setErr(
          `Unexpected sign-in step: ${res.nextStep?.signInStep ?? "unknown"}`,
        );
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setErr(null);
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Google sign-in failed");
    }
  }

  return (
    <PublicShell minimal>
      <div className="max-w-md mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="font-serif text-3xl md:text-4xl leading-tight">
            Welcome back.
          </h1>

          <Button
            variant="outline"
            onClick={onGoogle}
            className="w-full justify-center mt-7"
          >
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-7">
            <hr className="flex-1 border-0 h-px bg-[rgba(42,36,26,0.15)]" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-faint">
              or with email
            </span>
            <hr className="flex-1 border-0 h-px bg-[rgba(42,36,26,0.15)]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full justify-center mt-2"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            {err && (
              <div className="text-sm text-terracotta font-medium">{err}</div>
            )}
          </form>

          <p className="text-sm text-ink-soft mt-10">
            New here?{" "}
            <Link
              to="/signup"
              className="text-terracotta font-medium hover:underline"
            >
              Create an account
            </Link>
          </p>
        </motion.div>
      </div>
    </PublicShell>
  );
}
